import React, { useState, useEffect } from "react";
import { Paper, Typography, Alert, Box, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useUserContext } from "../context/UserContext";
import { getQREventById, getClassroomById, findActiveClassroomBookingNow, getTimetableEntryById } from "../context/Api";
import { calculateDistance, getUserGeolocation } from "../utils/geolocationUtils";
import QRScanner from "react-qr-barcode-scanner";
import PageNavBar from "../components/PageNavBar";

const QRScan = () => {
  const navigate = useNavigate();
  const { userRole, tenantId, selectedTenantId, refreshContext } = useUserContext();
  
  // For SuperAdmin, use selectedTenantId; for regular users, use tenantId
  // Add fallback to sessionStorage if context is not ready
  const contextTenantId = userRole === "SuperAdmin" ? selectedTenantId : tenantId;
  const sessionTenantId = userRole === "SuperAdmin" 
    ? sessionStorage.getItem("selectedTenantId") 
    : sessionStorage.getItem("tenantId");
  
  const effectiveTenantId = contextTenantId || sessionTenantId;
  
  const [error, setError] = useState("");
  const [scanned, setScanned] = useState(false);



  const onUpdate = async (err, result) => {
    if (scanned) return;
    if (err) return; // ignore intermittent decode errors
    if (!result?.text) return;
    try {
      const parsed = JSON.parse(result.text);
      if (!parsed?.t || !parsed?.id) {
        setError("Invalid QR code format");
        return;
      }

      // Check timestamp to prevent proxy (QR codes expire after 5 minutes)
      if (parsed.ts) {
        const now = Date.now();
        const qrTime = parsed.ts;
        const timeDiff = now - qrTime;
        const fiveMinutes = 5 * 60 * 1000;
        
        if (timeDiff > fiveMinutes) {
          setError("QR code has expired. Please scan a fresh QR code.");
          setScanned(false);
          return;
        }
      }

      // Check if user is authenticated - if not, redirect to external attendance page
      const isAuthenticated = sessionStorage.getItem("userEmail") || sessionStorage.getItem("userId");
      if (!isAuthenticated) {
        // Redirect to external attendance page with QR data
        const qrData = encodeURIComponent(result.text);
        window.location.href = `/external-attendance?data=${qrData}`;
        return;
      }

      setScanned(true);

      if (parsed.t === "lecture") {
        // Handle lecture/timetable entry QR code
        const finalTenantId = effectiveTenantId || 
          (userRole === "SuperAdmin" ? sessionStorage.getItem("selectedTenantId") : sessionStorage.getItem("tenantId"));
        
        if (!finalTenantId) {
          setError("No tenant ID available. Please select a tenant first or refresh and try again.");
          setScanned(false);
          return;
        }

        const timetableEntry = await getTimetableEntryById(parsed.id);
        if (!timetableEntry || !timetableEntry.active) {
          setError("Lecture not found or inactive");
          setScanned(false);
          return;
        }

        if (timetableEntry.tenantId !== finalTenantId) {
          setError("Lecture belongs to different tenant");
          setScanned(false);
          return;
        }

        // Get classroom for location validation
        const classroom = await getClassroomById(timetableEntry.classroomId);
        if (!classroom) {
          setError("Classroom not found");
          setScanned(false);
          return;
        }

        // Check if current time matches the lecture time
        const now = new Date();
        const currentWeekday = now.getDay();
        const [startH, startM] = timetableEntry.startTime.split(":").map(Number);
        const [endH, endM] = timetableEntry.endTime.split(":").map(Number);
        
        // Check weekday match
        if (currentWeekday !== timetableEntry.weekday) {
          const weekdayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
          setError(`This lecture is scheduled for ${weekdayNames[timetableEntry.weekday]}, not today`);
          setScanned(false);
          return;
        }

        // Check time window (allow 15 minutes before and after)
        const currentMinutes = now.getHours() * 60 + now.getMinutes();
        const startMinutes = startH * 60 + startM;
        const endMinutes = endH * 60 + endM;
        
        if (currentMinutes < startMinutes - 15 || currentMinutes > endMinutes + 15) {
          setError(`Lecture time is ${timetableEntry.startTime} - ${timetableEntry.endTime}. Current time is outside the allowed window.`);
          setScanned(false);
          return;
        }

        // Check date range if specified
        if (timetableEntry.startDate || timetableEntry.endDate) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          if (timetableEntry.startDate) {
            const startDate = new Date(timetableEntry.startDate);
            startDate.setHours(0, 0, 0, 0);
            if (today < startDate) {
              setError("This lecture has not started yet");
              setScanned(false);
              return;
            }
          }
          
          if (timetableEntry.endDate) {
            const endDate = new Date(timetableEntry.endDate);
            endDate.setHours(0, 0, 0, 0);
            if (today > endDate) {
              setError("This lecture has ended");
              setScanned(false);
              return;
            }
          }
        }

        // Validate location
        const geo = await getUserGeolocation();
        const dist = calculateDistance(geo.latitude, geo.longitude, classroom.latitude, classroom.longitude);
        if (dist > (classroom.radius || 100)) {
          setError("You are not at the classroom location");
          setScanned(false);
          return;
        }

        // Create a temporary booking-like object for timer
        sessionStorage.setItem("activeQREventId", timetableEntry.id);
        sessionStorage.setItem("activeQREvent", JSON.stringify({
          id: timetableEntry.id,
          latitude: classroom.latitude,
          longitude: classroom.longitude,
          radius: classroom.radius || 100,
          endTime: timetableEntry.endTime,
          date: now.toISOString().slice(0, 10),
          classroomId: classroom.id,
        }));
        navigate("/timer");
        return;
      }

      if (parsed.t === "classroom") {
        // Final fallback: check sessionStorage directly
        const finalTenantId = effectiveTenantId || 
          (userRole === "SuperAdmin" ? sessionStorage.getItem("selectedTenantId") : sessionStorage.getItem("tenantId"));
        
        if (!finalTenantId) {
          setError("No tenant ID available. Please select a tenant first or refresh and try again.");
          setScanned(false);
          return;
        }
        
        const classroom = await getClassroomById(parsed.id);
        
        if (!classroom) {
          setError("Classroom not found");
          setScanned(false);
          return;
        }
        
        if (classroom.tenantId !== finalTenantId) {
          setError(`Classroom belongs to different tenant. Expected: ${finalTenantId}, Found: ${classroom.tenantId}`);
          setScanned(false);
          return;
        }
        
        const now = new Date();
        const activeBooking = await findActiveClassroomBookingNow(finalTenantId, classroom.id);
        
        if (!activeBooking) {
          // Get current time for better error message
          const currentTime = now.toTimeString().slice(0, 8);
          const currentDate = now.toISOString().slice(0, 10);
          
          setError(`No active booking for this classroom now. Current time: ${currentTime} on ${currentDate}. Please check if there's a booking scheduled for this time.`);
          setScanned(false);
          return;
        }
        // Validate location proximity using classroom coordinates
        const geo = await getUserGeolocation();
        const dist = calculateDistance(geo.latitude, geo.longitude, classroom.latitude, classroom.longitude);
        if (dist > (classroom.radius || 100)) {
          setError("You are not at the classroom location");
          setScanned(false);
          return;
        }
        sessionStorage.setItem("activeQREventId", activeBooking.id);
        sessionStorage.setItem("activeQREvent", JSON.stringify({
          id: activeBooking.id,
          latitude: classroom.latitude,
          longitude: classroom.longitude,
          radius: classroom.radius || 100,
          endTime: activeBooking.endTime,
          date: activeBooking.date,
          classroomId: classroom.id,
        }));
        navigate("/timer");
        return;
      }

      if (parsed.t !== "qr_event") {
        setError("Invalid QR code");
        setScanned(false);
        return;
      }
      // Final fallback: check sessionStorage directly
      const finalTenantId = effectiveTenantId || 
        (userRole === "SuperAdmin" ? sessionStorage.getItem("selectedTenantId") : sessionStorage.getItem("tenantId"));
      
      if (!finalTenantId) {
        setError("No tenant ID available. Please select a tenant first or refresh and try again.");
        setScanned(false);
        return;
      }
      
      const event = await getQREventById(parsed.id);
      if (!event || event.tenantId !== finalTenantId || !event.active) {
        setError("Event not found or inactive");
        setScanned(false);
        return;
      }

      // Validate time window with proper 24-hour format handling
      const now = new Date();
      const today = event.date;
      
      // Parse start and end times properly
      const startTime = event.startTime;
      const endTime = event.endTime;
      
      // Create proper Date objects for start and end times
      const start = new Date(`${today}T${startTime}:00`);
      const end = new Date(`${today}T${endTime}:00`);
      
      // Handle case where end time is next day (e.g., 23:00 to 01:00)
      if (end <= start) {
        end.setDate(end.getDate() + 1);
      }
      
      if (now < start || now > end) {
        setError(`Event not within active time window. Event runs from ${startTime} to ${endTime} on ${today}`);
        setScanned(false);
        return;
      }

      // Validate location proximity for normal events; classroom bookings skip geolocation
      if (event.type !== "classroom") {
        const geo = await getUserGeolocation();
        const dist = calculateDistance(geo.latitude, geo.longitude, event.latitude, event.longitude);
        if (dist > (event.radius || 100)) {
          setError("You are not at the event location");
          setScanned(false);
          return;
        }
      }

      // Store event in session for Timer usage
      sessionStorage.setItem("activeQREventId", event.id);
      sessionStorage.setItem("activeQREvent", JSON.stringify({
        id: event.id,
        latitude: event.latitude,
        longitude: event.longitude,
        radius: event.radius || 100,
        endTime: event.endTime,
        date: event.date,
      }));

      navigate("/timer");
    } catch (e) {
      setError("Failed to process QR code");
      setScanned(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', position: 'relative' }}>
      <PageNavBar title="QR Scan" backTo="/dashboard" />
      <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, sm: 3 }, mt: 2 }}>
      {/* Warning when no tenant ID is available */}
      {!effectiveTenantId && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>No Tenant ID Available</strong><br/>
            {userRole === "SuperAdmin" 
              ? "Please select a tenant first by going to Classroom Management or Admin QR Event pages."
              : "Please make sure you are logged in properly. Try refreshing the page."
            }
          </Typography>
        </Alert>
      )}
      
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3, background: "rgba(255, 255, 255, 0.98)" }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>Scan QR Code</Typography>
        <Typography variant="body2" color="text.secondary">
          Align the QR code within the frame. The scan will start automatically.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ width: '100%', maxWidth: 640 }}>
            <Box sx={{ position: 'relative', width: '100%', pt: '100%', borderRadius: 2, overflow: 'hidden', boxShadow: 2 }}>
              <Box sx={{ position: 'absolute', inset: 0 }}>
                <QRScanner
                  width={undefined}
                  height={undefined}
                  onUpdate={onUpdate}
                />
              </Box>
            </Box>
          </Box>
        </Box>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button variant="outlined" onClick={() => setError("")}>Clear Message</Button>
        </Box>
      </Paper>
      </Box>
    </Box>
  );
};

export default QRScan;


